// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS } from "@mg/schemas/src/christo/askPuntt";
import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import {
  CaretDown,
  ChatCircle,
  CircleNotch,
  Plus,
  Users,
  XCircle,
} from "@phosphor-icons/react";
import { IconButton, SegmentedControl, Text } from "@radix-ui/themes";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentsList } from "./AskV2ReviewCommentsList";
import { AskV2ReviewComposerProvider } from "./AskV2ReviewComposerContext";
import { AskV2ReviewPanes } from "./AskV2ReviewPanes";
import { type RejectionScope } from "./rememberedSentence";
import {
  defaultActiveReviewCommentTags,
  isReviewCommentVisible,
  reviewCommentTags,
  type ReviewCommentTagId,
} from "./reviewCommentTags";
import {
  useAskPunttReview,
  useCreateAskPunttReviewComment,
  useUpdateAskPunttReviewComment,
} from "./useAskPunttReview";
import {
  type AskV2ReviewLeftPane,
  useAskV2ReviewWorkspace,
} from "./useAskV2ReviewWorkspace";

import {
  AskPunttArtifactProvider,
  AskPunttArtworkViewer,
  type AskPunttArtifactContextValue,
  type HumanCommentSubmitInput,
  reviewCommentTitle,
  useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { useAnalytics } from "../../utils/analytics";
import { useAutoScroll } from "../../utils/useAutoScroll";
import {
  rejectedReviewCommentCount,
  reviewCommentsForPanel,
} from "../../utils/visibleReviewComments";
import { TextSelectionToolbar } from "../transport/TextSelectionToolbar";
import { AskV2Thread } from "../transport/Thread";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type ReviewVersionOption = {
  reviewId: string;
  title: string;
};

type FeedMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

type AskV2ReviewWorkspaceProps = {
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  transport: AskV2TransportApi;
  versionOptions?: readonly ReviewVersionOption[];
  feedPresentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  artifactContext?: AskPunttArtifactContextValue;
  /** Chat composer pinned under the left findings column only. */
  composer?: ReactNode;
};

function ReviewPaneTabs({
  leftPane,
  commentCount,
  onChange,
  "data-testid": testId = "ask-v2-review-pane-tabs",
}: {
  leftPane: AskV2ReviewLeftPane;
  commentCount: number;
  onChange: (pane: AskV2ReviewLeftPane) => void;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");

  return (
    <div data-testid={testId}>
      <SegmentedControl.Root
        value={leftPane}
        radius="full"
        // why: this switcher is a Radix SegmentedControl -- a ToggleGroup
        // type="single", whose items render `role="radio"`, never `role="tab"`.
        // The wrapper used to claim `role="tablist"`, so the review pane
        // switcher was a tab list owning zero tabs (axe
        // aria-required-children), and the one accessible name it has sat on
        // that wrapper instead of on the control a reader operates -- the
        // radios were announced with no group name and no "1 of 2" position.
        // AskV2ProjectTabs is the real tablist-with-tabs case; this is a radio
        // group, so name it as one and let the radios be owned by it.
        role="radiogroup"
        aria-label={t("askV2.review.paneTabsLabel")}
        onValueChange={(value) => {
          if (value === "feed" || value === "comments") {
            onChange(value);
          }
        }}
      >
        <SegmentedControl.Item
          value="feed"
          data-testid="ask-v2-review-tab-feed"
        >
          <span className="flex items-center gap-1.5">
            <Users size={16} aria-hidden="true" />
            {t("askV2.review.feedTab")}
          </span>
        </SegmentedControl.Item>
        <SegmentedControl.Item
          value="comments"
          data-testid="ask-v2-review-tab-comments"
        >
          <span className="flex items-center gap-1.5">
            <span className="relative inline-flex shrink-0" aria-hidden="true">
              <ChatCircle size={16} />
              <Plus
                size={8}
                weight="bold"
                className="absolute -bottom-0.5 -right-0.5"
              />
            </span>
            {t("askV2.review.commentsTab", { count: commentCount })}
          </span>
        </SegmentedControl.Item>
      </SegmentedControl.Root>
    </div>
  );
}

function VersionPicker({
  review,
  options,
  onSelect,
}: {
  review: AskPunttReview;
  options: readonly ReviewVersionOption[];
  onSelect: (reviewId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const disabled = options.length <= 1;

  // why: this menu is a hand-rolled popup, not a Radix one, so nothing ever
  // dismissed it. Opening it and then reading on -- clicking the artwork, a
  // finding, the close control -- left it hanging over the header at z-20
  // until the trigger happened to be pressed a second time, and Escape did
  // nothing at all. Capture phase because the artwork pane takes pointerdown
  // for pan and pin placement; a target that stops propagation must not be
  // able to keep the menu open.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // A reader who tabbed into the list would otherwise be dropped on
      // <body> when the option they were on unmounts.
      triggerRef.current?.focus();
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("askV2.review.versionPickerLabel")}
        data-testid="ask-v2-review-version-picker"
        className="flex max-w-[220px] items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 disabled:cursor-default disabled:opacity-70"
        onClick={() => !disabled && setOpen((current) => !current)}
      >
        <span className="truncate">{review.title}</span>
        <CaretDown size={14} aria-hidden="true" />
      </button>
      {open && options.length > 1 ? (
        <ul
          className="absolute left-0 top-full z-20 mt-1 min-w-[220px] rounded-md border border-puntt-neutral-gray-5 bg-base-white py-1 shadow-lg"
          role="listbox"
          // why: the popup that opens is a second thing to name, not the same
          // thing as the trigger -- unnamed, a reader who opens it is told only
          // "list box" and has to infer what it is picking between.
          aria-label={t("askV2.review.versionPickerLabel")}
        >
          {options.map((option) => (
            // why: a `listbox` may own only `option` (or `group`). Left as a
            // plain `<li>` each wrapper is a `listitem`, so this menu reached a
            // reader as a two-item list with a button inside each -- no "option
            // 1 of 2", and the `aria-selected` that marks the version currently
            // open sat on an element the listbox does not own. That is the same
            // axe aria-required-children failure ReviewPaneTabs above was fixed
            // for. `role="none"` drops the wrapper out of the accessibility
            // tree so the listbox owns the option buttons directly, and leaves
            // focus, clicks and the testid on the button where they already are.
            <li key={option.reviewId} role="none">
              <button
                type="button"
                role="option"
                aria-selected={option.reviewId === review.reviewId}
                className="block w-full px-3 py-2 text-left text-[13px] hover:bg-puntt-neutral-gray-2"
                data-testid={`ask-v2-review-version-option-${option.reviewId}`}
                onClick={() => {
                  setOpen(false);
                  onSelect(option.reviewId);
                }}
              >
                {option.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function AskV2ReviewWorkspace({
  workspace,
  transport,
  versionOptions = [],
  feedPresentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  authorNameByUserId,
  artifactContext,
  composer,
}: AskV2ReviewWorkspaceProps) {
  const { t } = useTranslation("ui");
  const reviewQuery = useAskPunttReview(workspace.reviewId);
  const review = reviewQuery.data?.review;
  const artworkFiles = useMemo(
    () => mapReviewFilesToAssets(review?.files ?? []),
    [review?.files],
  );
  const contextFiles = useMemo(
    () => mapReviewFilesToAssets(review?.contextAssets ?? []),
    [review?.contextAssets],
  );

  // why: syncing comment selection to the /comments/:id route remounts the page
  // and resets the findings list scroll position; keep selection local while open.
  const artworkFocus = useReviewArtworkFocus({
    comments: review?.comments,
    initialCommentId: workspace.commentId,
  });

  const [activeTags, setActiveTags] = useState<Set<ReviewCommentTagId>>(
    defaultActiveReviewCommentTags,
  );
  const [includeRejectedComments, setIncludeRejectedComments] = useState(false);
  const [judgmentFailed, setJudgmentFailed] = useState(false);

  const tagFilteredComments = useMemo(() => {
    if (!review) return [];
    return review.comments.filter((comment) =>
      isReviewCommentVisible(comment, activeTags),
    );
  }, [activeTags, review]);

  const panelComments = useMemo(
    () => reviewCommentsForPanel(tagFilteredComments, includeRejectedComments),
    [includeRejectedComments, tagFilteredComments],
  );

  const rejectedCount = useMemo(
    () => rejectedReviewCommentCount(tagFilteredComments),
    [tagFilteredComments],
  );

  const commentNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of panelComments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [panelComments]);

  // why: tag persistence on review comments lands separately — the filter must
  // still render so users can narrow once tags arrive (untagged rows stay visible).
  const showTagFilter = useMemo(
    () => (review?.comments ?? []).some((comment) => !comment.isDeleted),
    [review?.comments],
  );

  const {
    selectedCommentId,
    selectComment,
    clearComment,
    artworkPaneRef,
    artworkHeaderControls,
    setArtworkHeaderControls,
  } = artworkFocus;

  const { mutateAsync: createReviewComment } = useCreateAskPunttReviewComment(
    workspace.reviewId,
  );
  const { mutateAsync: updateReviewComment, isPending: isJudgmentPending } =
    useUpdateAskPunttReviewComment(workspace.reviewId);
  const analytics = useAnalytics();
  const [pendingFocusCommentId, setPendingFocusCommentId] = useState<
    string | null
  >(null);
  const { setLeftPane } = workspace;
  const { setCommentContext, clearCommentContexts, removeCommentContext } =
    transport;
  const handleRemoveCommentContext = useCallback(
    (id: string) => {
      removeCommentContext(id);
      clearComment();
    },
    [clearComment, removeCommentContext],
  );
  const handleSubmitHumanComment = useCallback(
    async (input: HumanCommentSubmitInput) => {
      const { comment } = await createReviewComment({
        description: input.description,
        rel_x: input.relX,
        rel_y: input.relY,
        page_num: input.page,
      });
      setPendingFocusCommentId(comment._id);
      setLeftPane("comments");
    },
    [createReviewComment, setLeftPane],
  );

  // why: the saved comment reaches `review.comments` one render after the
  // mutation resolves; selecting it synchronously misses the focus lookup.
  useEffect(() => {
    if (pendingFocusCommentId == null) return;
    if (!review?.comments.some((c) => c._id === pendingFocusCommentId)) {
      return;
    }
    selectComment(pendingFocusCommentId);
    setPendingFocusCommentId(null);
  }, [pendingFocusCommentId, review?.comments, selectComment]);

  useEffect(() => {
    if (!selectedCommentId || !review) return;
    const comment = review.comments.find(
      (item) => item._id === selectedCommentId,
    );
    const onPanel = panelComments.some((c) => c._id === selectedCommentId);
    if (!comment || !isReviewCommentVisible(comment, activeTags) || !onPanel) {
      clearComment();
    }
  }, [activeTags, clearComment, panelComments, review, selectedCommentId]);

  useEffect(() => {
    if (!selectedCommentId || !review) {
      clearCommentContexts();
      return;
    }
    const comment = review.comments.find(
      (item) => item._id === selectedCommentId,
    );
    const onPanel = panelComments.some((c) => c._id === selectedCommentId);
    if (!comment || !isReviewCommentVisible(comment, activeTags) || !onPanel) {
      clearCommentContexts();
      return;
    }
    const number = commentNumbers[selectedCommentId] ?? 0;
    const title = reviewCommentTitle(comment);
    const normalizedTitle =
      title.length <= ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS
        ? title
        : `${title.slice(0, ASK_PUNTT_COMMENT_CONTEXT_TITLE_MAX_CHARS - 3)}...`;
    const tags = reviewCommentTags(comment);
    setCommentContext({
      id: `review-comment-${selectedCommentId}`,
      commentId: selectedCommentId,
      reviewId: review.reviewId,
      number,
      title: normalizedTitle,
      tag: tags[0],
    });
  }, [
    activeTags,
    clearCommentContexts,
    commentNumbers,
    panelComments,
    review,
    selectedCommentId,
    setCommentContext,
  ]);

  const seenSendingUserIdsRef = useRef(new Set<string>());

  useEffect(() => {
    for (const message of transport.messages) {
      if (message.role !== "user" || message.status !== "sending") continue;
      if (seenSendingUserIdsRef.current.has(message.clientId)) continue;
      seenSendingUserIdsRef.current.add(message.clientId);
      if ((message.commentContexts?.length ?? 0) > 0) {
        clearComment();
      }
      if (workspace.leftPane === "comments") {
        workspace.setLeftPane("feed");
      }
    }
  }, [
    clearComment,
    transport.messages,
    workspace.leftPane,
    workspace.setLeftPane,
  ]);

  const handleToggleTag = (tag: ReviewCommentTagId, checked: boolean) => {
    setActiveTags((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(tag);
      } else {
        next.delete(tag);
      }
      return next;
    });
  };

  const handleJudgeComment = useCallback(
    async (
      commentId: string,
      liked: boolean | null,
      scope?: RejectionScope,
    ) => {
      const visibleList = panelComments;
      const index = visibleList.findIndex((item) => item._id === commentId);
      const previousLiked =
        visibleList.find((item) => item._id === commentId)?.liked ?? null;
      // why: `liked: null` arrives from Restore on a rejected finding AND from
      // a second Confirm press that clears a confirmation; only the first is a restore.
      const isRestore = liked === null && previousLiked === false;

      analytics.capture("ask_puntt_feedback", {
        feedback:
          liked === true
            ? "comment_thumbs_up"
            : liked === false
              ? "comment_thumbs_down"
              : isRestore
                ? "comment_restore"
                : "comment_unconfirm",
        comment_id: commentId,
        review_id: workspace.reviewId,
      });

      // The scope rides with the verdict rather than in a second call: two
      // writes could land a rejection with no scope, which is the one state
      // that binds every brand.
      setJudgmentFailed(false);
      try {
        await updateReviewComment({
          commentId,
          body: scope ? { liked, judgmentScope: scope } : { liked },
        });
      } catch {
        // why: stay on the finding and say so. Nothing was written and the
        // card keeps its prior state; advancing would read as saved, and the
        // reject prompt has already closed, so without this line a failed
        // save is invisible.
        setJudgmentFailed(true);
        return;
      }

      if (liked === null) {
        // why: restore exits rejected mode, so other rejected findings hide until toggled again
        if (isRestore) setIncludeRejectedComments(false);
        return;
      }

      if (liked === true && previousLiked !== true) {
        const next = visibleList[index + 1];
        if (next) selectComment(next._id);
        return;
      }

      if (liked === false) {
        const next =
          visibleList[index + 1] ?? visibleList[index - 1] ?? undefined;
        if (next != null && next._id !== commentId) {
          selectComment(next._id);
        } else {
          clearComment();
        }
      }
    },
    [
      analytics,
      clearComment,
      panelComments,
      selectComment,
      updateReviewComment,
      workspace.reviewId,
    ],
  );

  const resolvedVersionOptions = useMemo(() => {
    if (versionOptions.length > 0) return versionOptions;
    if (!review) return [];
    return [{ reviewId: review.reviewId, title: review.title }];
  }, [review, versionOptions]);

  const {
    scrollContainerRef: feedScrollContainerRef,
    contentRef: feedContentRef,
    handleScroll: handleFeedScroll,
  } = useAutoScroll(
    // why: the feed container is mounted only on the feed pane, but the
    // composer that sends into it is on both. Without leftPane here, a send
    // made from the comments pane arms the pin against a container that does
    // not exist yet, and nothing re-runs the effect when the pane switches —
    // so the feed opens at the top, on the oldest messages.
    [transport.displayMessages, workspace.leftPane],
    transport.chatId,
    {
      enabled: !transport.inFlight,
      // why: the sent turn can render while the sticky scroll guard is still
      // latched, and nothing in ask-v2 clears it on send — so without an
      // explicit send signal the turn lands below the fold and the reader has
      // to scroll to find it. (The `enabled` hold is not the blocker on that
      // first commit: send() writes `sending` and the optimistic row in one
      // mutate and does not recompute `inFlight`.)
      sentSignal: transport.sending,
    },
  );

  if (reviewQuery.isLoading) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        data-testid="ask-v2-review-loading"
      >
        <CircleNotch
          size={24}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (reviewQuery.isError || !review) {
    return (
      <div
        className="flex flex-1 items-center justify-center px-6 text-center text-sm text-puntt-neutral-gray-10"
        data-testid="ask-v2-review-error"
      >
        {t("askV2.review.loadError")}
      </div>
    );
  }

  return (
    <div
      className="flex size-full min-h-0 flex-1 overflow-hidden"
      data-testid="ask-v2-review-workspace"
      // why: a `<div>` with no role is `role="generic"`, and ARIA prohibits
      // `aria-label` there (axe `aria-prohibited-attr`) -- the name was
      // computed and then dropped, so both halves of this screen reached a
      // reader as unnamed generic boxes and the artwork pane below could not
      // be told apart from the findings column. The two names this component
      // already pays for only exist once something owns them; `region` is what
      // the findings list itself uses (`<section aria-label>` in
      // AskV2ReviewCommentsList) and what CommentLoginWall/TimeSavedBubble use
      // for the same job.
      role="region"
      aria-label={t("askV2.review.workspaceLabel")}
    >
      <AskV2ReviewPanes
        abortSignal={review.reviewId}
        left={
          <div className="ask-puntt-surface flex h-full min-h-0 min-w-0 flex-col">
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
              {workspace.leftPane === "comments" && judgmentFailed ? (
                <Text
                  as="p"
                  size="2"
                  color="red"
                  role="alert"
                  className="px-4 pt-3"
                  data-testid="ask-v2-review-judgment-error"
                >
                  {t("assetViewer.judgmentSaveFailed")}
                </Text>
              ) : null}
              {workspace.leftPane === "comments" ? (
                <AskV2ReviewCommentsList
                  comments={panelComments}
                  blocked={review.blocked ?? []}
                  commentNumbers={commentNumbers}
                  contextFiles={contextFiles}
                  reviewFiles={artworkFiles}
                  selectedCommentId={selectedCommentId}
                  authorNameByUserId={authorNameByUserId}
                  currentUserId={currentUserId}
                  currentUserName={feedActorName}
                  activeTags={activeTags}
                  showTagFilter={showTagFilter}
                  onToggleTag={handleToggleTag}
                  includeRejectedComments={includeRejectedComments}
                  rejectedCommentCount={rejectedCount}
                  onToggleIncludeRejected={() =>
                    setIncludeRejectedComments((current) => !current)
                  }
                  judgmentPending={isJudgmentPending}
                  onJudge={handleJudgeComment}
                  onSelect={selectComment}
                  onClear={clearComment}
                />
              ) : (
                <div
                  ref={feedScrollContainerRef}
                  onScroll={handleFeedScroll}
                  className="relative min-h-0 flex-1 overflow-y-auto px-4 py-6"
                  data-testid="ask-v2-review-feed-scroll"
                >
                  <TextSelectionToolbar
                    containerRef={feedScrollContainerRef}
                    onAddContext={transport.addQuotedContext}
                  />
                  <div ref={feedContentRef}>
                    {artifactContext ? (
                      <AskPunttArtifactProvider value={artifactContext}>
                        <AskV2Thread
                          transport={transport}
                          presentation={feedPresentation}
                          feedActorName={feedActorName}
                          feedActorAvatar={feedActorAvatar}
                          feedMembers={feedMembers}
                          currentUserId={currentUserId}
                        />
                      </AskPunttArtifactProvider>
                    ) : (
                      <AskV2Thread
                        transport={transport}
                        presentation={feedPresentation}
                        feedActorName={feedActorName}
                        feedActorAvatar={feedActorAvatar}
                        feedMembers={feedMembers}
                        currentUserId={currentUserId}
                      />
                    )}
                  </div>
                </div>
              )}
              {composer ? (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-5 bg-[linear-gradient(to_top,var(--ask-puntt-surface-bg)_15%,transparent)]"
                  data-testid="ask-v2-review-comment-list-fade"
                />
              ) : null}
            </div>
            {composer ? (
              <div
                className="shrink-0 p-4"
                data-testid="ask-v2-review-composer"
              >
                <AskV2ReviewComposerProvider
                  onRemoveCommentContext={handleRemoveCommentContext}
                >
                  {composer}
                </AskV2ReviewComposerProvider>
              </div>
            ) : null}
          </div>
        }
        right={
          <div
            className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden bg-puntt-neutral-gray-2"
            data-testid="ask-v2-review-artwork"
            // why: same prohibited-attribute drop as the workspace wrapper --
            // "Artwork" is the only thing telling this pane apart from the
            // findings column, and a bare `<div>` may not carry a name.
            role="region"
            aria-label={t("askV2.review.artworkLabel")}
          >
            <div className="flex items-center gap-3 border-b border-puntt-neutral-gray-5 bg-base-white px-4 py-2">
              <IconButton
                variant="ghost"
                color="gray"
                size="1"
                data-testid="ask-v2-review-close"
                aria-label={t("askV2.review.closeLabel")}
                onClick={workspace.closeReview}
              >
                <XCircle size={16} weight="bold" aria-hidden="true" />
              </IconButton>
              <VersionPicker
                review={review}
                options={resolvedVersionOptions}
                onSelect={workspace.switchReview}
              />
              <ReviewPaneTabs
                data-testid="ask-v2-review-pane-tabs"
                leftPane={workspace.leftPane}
                // why: the badge must count the same array the findings list and
                // the artwork pins render. Counting `review.comments` instead let
                // the tab read "Comments (3)" beside a list of 2 after a reject.
                commentCount={panelComments.length}
                onChange={workspace.setLeftPane}
              />
              <div
                className="ml-auto flex items-center gap-2"
                data-testid="ask-v2-review-header-controls"
              >
                {artworkHeaderControls}
              </div>
            </div>
            <div className="min-h-0 flex-1">
              <AskPunttArtworkViewer
                ref={artworkPaneRef}
                reviewId={review.reviewId}
                title={review.title}
                files={artworkFiles}
                comments={panelComments}
                activeCommentId={selectedCommentId}
                onHeaderControlsChange={setArtworkHeaderControls}
                onCommentClick={(comment) => {
                  workspace.setLeftPane("comments");
                  selectComment(comment._id);
                }}
                onArtworkBackgroundClick={() => {
                  if (selectedCommentId != null) {
                    clearComment();
                  }
                }}
                onSubmitHumanComment={handleSubmitHumanComment}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}
